import { forwardRef, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

const FileUploader = ({ images, setImages, selectedCategory }) => {
  const { t } = useTranslation();

  const baseStyle = {
    borderColor: "#949494",
    backgroundColor: "#FFF",
    color: "#000",
    transition: "border .24s ease-in-out",
  };
  const focusedStyle = { borderColor: "#3979B9", backgroundColor: "#F0F7FE" };
  const rejectStyle = { borderColor: "#ff1744" };

  const [showToast, setShowToast] = useState(false);
  const [message, setMessage] = useState();
  const onDropAccepted = (acceptedFiles) => {
    if (selectedCategory && selectedCategory !== "all") {
      setShowToast(false);
      if (acceptedFiles.length >= 1) {
        setImages((prevImages) => [...prevImages, ...acceptedFiles]);
      }
    } else {
      setMessage("Please selected category");
      setShowToast(true);
    }
  };

  function fileAlreadyAdded(file) {
    if (images.find((image) => image.path === file.path)) {
      setMessage(`${file.name} ${t("FileUpload-message")}`);
      setShowToast(true);
      return {
        code: "file-already-added",
        message: `${file.name} ${t("FileUpload-message")}`,
      };
    }

    return null;
  }
  const onDropRejected = () => {
    setMessage(t("FileUpload-errro-message"));
    setShowToast(true);
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        "image/jpeg": [],
        "application/pdf": [],
        "image/png": [],
        "image/tiff": [],
      },
      onDropAccepted,
      validator: fileAlreadyAdded,
      onDropRejected,
      maxSize: 10000000,
    });

  const handleClick = (file) => {
    const newImages = images.filter((image) => image !== file);

    setImages(newImages);
  };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? focusedStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const files = images?.map((file) => {
    const formatBytes = (size) => {
      let i;
      const units = ["B", "KB", "MB", "GB", "TB"];

      for (i = 0; size >= 1024 && i < 4; i++) {
        size /= 1024;
      }

      return size.toFixed(0) + units[i];
    };

    let progressPercentage = Number(((file.size * 100) / 10000000).toFixed(0));

    return (
      <li
        className="ui-card p-4 mb-4 border border-ui-gray-medium "
        key={file.path}
      >
        <div className="flex justify-between grow">
          <div className="flex items-center">
            <svg
              className="mt-[5px]"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <use href="#ui-icon-file"></use>
            </svg>
            <div className="pl-1">{file.path}</div>
          </div>
          <div className="flex items-center">
            {/* <div className="mx-5">{formatBytes(file.size)}</div> */}
            <button onClick={() => handleClick(file)}>X</button>
          </div>
        </div>
        {/* <div className="pl-1 pt-0.5">{selectedCategory}</div> */}
        <div>
          <div className="flex justify-between grow ">
            <div className="flex items-center">
              <div className="pl-1 text-xxs"> Uploading...</div>
            </div>
            <div className="flex items-center">
              {/* <div className="mx-5">{formatBytes(file.size)}</div> */}
              <div>{`${progressPercentage}%`}</div>
            </div>
          </div>

          <div className="w-fullrounded-full w-full bg-ui-gray-light">
            <div
              class="bg-ui-blue text-xs font-medium text-center p-0.5 leading-none rounded-full"
              style={{
                width: `${progressPercentage}%`,
              }}
            ></div>
          </div>
        </div>
      </li>
    );
  });

  return (
    <>
      {showToast && (
        <div className={`top-0 lg:top-[120px] z-20 pb-4`}>
          <div
            className={`ui-card mt-4 p-4 relative xl:p-6 border-l-8 shadow-xl border-ui-red bg-red-200`}
          >
            <div className="flex flex-row">
              <div className="flex items-center mr-4">
                <svg
                  className={`ui-icon ring-8 rounded-full w-6 h-6 content-center border-ui-red`}
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href={"#ui-icon-circle-solid-exclamation"}></use>
                </svg>
              </div>
              <div>
                <div className="flex flex-row justify-between">
                  <h2 className="text-lg font-body font-normal my-auto">
                    {message}
                  </h2>
                  <button
                    className="ui-default-dialog-close"
                    aria-label="close toast alert"
                    onClick={() => setShowToast(false)}
                  >
                    <svg
                      className={`w-5 h-5 border-ui-red`}
                      focusable="false"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-red-x"></use>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4">
        <div
          className="flex flex-1 flex-col items-center p-14 border-2 background-white rounded-sm border-dashed outline-none "
          {...getRootProps({ style })}
        >
          <input {...getInputProps()} />
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.9999 34C13.5058 34.0017 11.1011 33.0713 9.25765 31.3914C7.41418 29.7115 6.265 27.4034 6.03558 24.9198C5.80616 22.4363 6.51308 19.9567 8.01761 17.9676C9.52215 15.9784 11.7157 14.6233 14.1679 14.168C14.5986 11.8753 15.817 9.80528 17.6123 8.31581C19.4077 6.82634 21.6671 6.01111 23.9999 6.01111C26.3327 6.01111 28.5921 6.82634 30.3874 8.31581C32.1828 9.80528 33.4012 11.8753 33.8319 14.168C36.2759 14.6325 38.4591 15.9909 39.9556 17.9782C41.452 19.9655 42.1544 22.4391 41.9255 24.9163C41.6967 27.3935 40.5531 29.6965 38.7179 31.376C36.8827 33.0556 34.4876 33.9911 31.9999 34M17.9999 24L23.9999 18M23.9999 18L29.9999 24M23.9999 18V42"
              stroke={`${isFocused ? "#2196f3" : "#767676"}`}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {isDragReject ? (
            <>
              <p className="text-lg text-theme-text-secondary font-semibold">
                {t("FileUpload-not-supported")}
              </p>
              <p className="text-sm text-theme-text-secondary font-normal">
                {t("FileUpload-allowed-files")}
              </p>
            </>
          ) : (
            <>
              <p className="text-lg text-theme-text-secondary font-semibold">
                {t("FileUpload-upload1")}{" "}
                <button className="underline text-theme-secondary">
                  {t("FileUpload-upload2")}
                </button>
              </p>
              <p className="text-sm text-theme-text-secondary font-normal">
                {t("FileUpload-allowed-files")}
              </p>
            </>
          )}
        </div>
        {files && <ul className="mt-7">{files}</ul>}
      </div>
    </>
  );
};

export default FileUploader;
